/**
 * Manual flight control — the three stage layouts.
 *
 * The operator flies the same gesture at three sizes: a full map with the
 * picture in the corner, map and picture side by side, or the picture as the
 * stage with the control reduced to a locked minimap. Only the ZOOM changes
 * between them — the vector is normalised to the speed ring, not to map
 * distance (`docs/PLANS/manual-flight-control/README.md`), so the surface is
 * re-hosted at three sizes rather than forked into three controls.
 *
 * Everything here is a plain value or a pure function: the layout vocabulary,
 * the operator's persisted choice, and the one geometry decision the layouts
 * make (how big the minimap is). No React, no DOM, so the rules can be tested
 * without rendering a COP.
 */

/** Wire value too: this string is what `control_release` reports for the audit. */
export type ManualStageLayout = 'map' | 'split' | 'video';

/** Ordered as the switcher renders them, widest map first. */
export const MANUAL_STAGE_LAYOUTS: readonly ManualStageLayout[] = ['map', 'split', 'video'];

/** The default. A new operator gets the full map and the picture in the corner. */
export const DEFAULT_MANUAL_STAGE_LAYOUT: ManualStageLayout = 'map';

/**
 * What each layout is, in words. Every state carries a word and not only a
 * position in a row of three buttons — the switcher is announced with these.
 */
export const MANUAL_LAYOUT_LABEL: Record<ManualStageLayout, string> = {
  map: 'MAP',
  split: 'SPLIT',
  video: 'VIDEO',
};

export const MANUAL_LAYOUT_DESCRIPTION: Record<ManualStageLayout, string> = {
  map: 'Full map control surface, video in the corner',
  split: 'Map and video side by side',
  video: 'Video on the stage, control minimap locked on the aircraft',
};

/**
 * Edge of the control minimap in the VIDEO layout, CSS px, on a MOUSE.
 *
 * Square, and inside the 240–300 px band the feature spec fixes: the shorter
 * side is what `ringGeometry` measures, so a square spends every pixel on the
 * ring instead of on a dimension the control cannot use. 280 leaves the
 * contract's 240 px minimum a 40 px margin, so the surface does not flip to
 * display-only the first time a border or a scrollbar takes a few pixels.
 */
export const MANUAL_MINIMAP_PX = 280;

/**
 * The same box on a FINGER, where the contract's minimum control surface is
 * 280 px rather than 240.
 *
 * It has to be bigger than 280, not equal to it. The panel is `border-box` with
 * a 2 px border, so its usable interior — which is what the control surface
 * measures and what `ringGeometry` judges — is 4 px smaller than the box. At a
 * flat 280 the VIDEO layout would hand a tablet operator a 276 px interior and
 * a display-only control: the one layout where the minimap IS the control would
 * be the one layout they could not fly in. 320 clears the minimum with room for
 * a border, a scrollbar and a rounding.
 */
export const MANUAL_MINIMAP_TOUCH_PX = 320;

/** Edge of the control minimap for the pointer actually driving it. */
export function manualMinimapPx(pointer: 'desktop' | 'touch' = 'desktop'): number {
  return pointer === 'touch' ? MANUAL_MINIMAP_TOUCH_PX : MANUAL_MINIMAP_PX;
}

/** Width of the corner picture-in-picture in the MAP layout, CSS px. */
export const MANUAL_PIP_WIDTH_PX = 288;

const STORAGE_PREFIX = 'manual_flight_layout';

/**
 * Per-operator, because the choice is a working preference and a shared console
 * is a real deployment: two operators signing into the same browser must not
 * inherit each other's stage. Keyed by whatever stable operator identity the
 * caller has (email today); with none, one shared anonymous slot.
 */
export function manualLayoutStorageKey(operator?: string | null): string {
  const who = operator?.trim();
  return who ? `${STORAGE_PREFIX}:${who}` : STORAGE_PREFIX;
}

export function isManualStageLayout(value: unknown): value is ManualStageLayout {
  return typeof value === 'string' && (MANUAL_STAGE_LAYOUTS as readonly string[]).includes(value);
}

/**
 * The operator's stored layout, or the default.
 *
 * Never throws: `localStorage` can throw outright (privacy mode, locked-down
 * enterprise policy) and a stored value can be anything at all if some other
 * build wrote it. A layout is a preference — failing to read one must never
 * stop an operator taking manual control.
 */
export function readManualLayout(operator?: string | null): ManualStageLayout {
  if (typeof window === 'undefined') return DEFAULT_MANUAL_STAGE_LAYOUT;
  try {
    const stored = window.localStorage.getItem(manualLayoutStorageKey(operator));
    return isManualStageLayout(stored) ? stored : DEFAULT_MANUAL_STAGE_LAYOUT;
  } catch {
    return DEFAULT_MANUAL_STAGE_LAYOUT;
  }
}

/** Returns false when the choice could not be persisted, so a caller can say so. */
export function writeManualLayout(layout: ManualStageLayout, operator?: string | null): boolean {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(manualLayoutStorageKey(operator), layout);
    return true;
  } catch {
    return false;
  }
}
